/**
 * Maps compare API change paths onto product-builder form field keys
 * so changed inputs can be highlighted in read-only compare view.
 */

/** Snapshot / item paths → builder form field keys (product-level). */
const PRODUCT_PATH_TO_FIELD = Object.freeze({
    'item.product_name': 'productName',
    'item.product_code': 'productCode',
    'item.measurement_unit': 'measurementUnit',
    'item.description': 'productNotes',
    'item.visibleInOfferApp': 'visibleInOfferApp',
    'item.status': 'status',
    'item.price': 'listPriceRonIncVatFinal',
    list_price: 'listPriceRonIncVatFinal',
    'pricing_detail.productNotes': 'productNotes',
    'pricing_detail.notes': 'notes',
    'pricing_detail.manufacturerName': 'manufacturerName',
    'pricing_detail.manufacturerCode': 'manufacturerCode',
    'pricing_detail.serialNumber': 'serialNumber',
    'pricing_detail.manufacturerProductCode': 'manufacturerProductCode',
    'pricing_detail.manufacturerProductName': 'manufacturerProductName',
    'pricing_detail.weight': 'weight',
    'pricing_detail.characteristics': 'productNotes',
    'pricing_detail.nomenclatureCode': 'nomenclatureCode',
    'pricing_detail.chapterCode': 'chapterCode',
    'pricing_detail.chapterName': 'chapterName',
    'pricing_detail.subchapterCode': 'subchapterCode',
    'pricing_detail.subchapterName': 'subchapterName',
    'pricing_detail.progressiveNumber': 'progressiveNumber',
    'pricing_detail.isCompositeProduct': 'isCompositeProduct',
    'pricing_detail.system1': 'systems',
    'pricing_detail.system2': 'systems',
    'pricing_detail.system3': 'systems',
    'pricing_detail.systems': 'systems',
    'pricing_detail.systemSubcategories': 'systems',
    category_assignments: 'systems',
});

/** Leaf field names under pricing_detail (non-furnizor) → form keys. */
const PRICING_DETAIL_LEAF_TO_FIELD = Object.freeze({
    productNotes: 'productNotes',
    notes: 'notes',
    manufacturerName: 'manufacturerName',
    manufacturerCode: 'manufacturerCode',
    serialNumber: 'serialNumber',
    manufacturerProductCode: 'manufacturerProductCode',
    manufacturerProductName: 'manufacturerProductName',
    weight: 'weight',
    characteristics: 'productNotes',
    nomenclatureCode: 'nomenclatureCode',
    chapterCode: 'chapterCode',
    chapterName: 'chapterName',
    subchapterCode: 'subchapterCode',
    subchapterName: 'subchapterName',
    progressiveNumber: 'progressiveNumber',
    isCompositeProduct: 'isCompositeProduct',
    system1: 'systems',
    system2: 'systems',
    system3: 'systems',
    systems: 'systems',
    systemSubcategories: 'systems',
    supplierListPriceNew: 'supplierListPriceNew',
    supplierListPriceOld: 'supplierListPriceNew',
    currency: 'currency',
    paymentTermDays: 'paymentTermDays',
    discount1: 'discounts.0',
    discount2: 'discounts.1',
    cashDiscount: 'discounts.2',
    marketingDiscount: 'discounts.3',
    transportCostRate: 'transportCostRate',
    transportCostEstimated: 'transportCostRate',
    stampDutyAmount: 'stampDutyAmount',
    stampDutyBreakdown: 'stampDutyAmount',
    markupReference: 'markupReference',
    markupActual: 'markupActual',
    listPriceRonIncVatReference: 'listPriceRonIncVatReference',
    listPriceRonIncVatFinal: 'listPriceRonIncVatFinal',
    overrideListPriceRonIncVatFinal: 'listPriceRonIncVatFinal',
    overrideListPriceRonIncVatFinalValue: 'listPriceRonIncVatFinal',
    manualListPriceRonIncVatFinal: 'listPriceRonIncVatFinal',
    useManualFinalPrice: 'listPriceRonIncVatFinal',
    priceAdjustmentRatio: 'listPriceRonIncVatFinal',
    entryPriceRonExVat: 'entryPriceRonExVat',
    entryPriceRonIncVat: 'entryPriceRonIncVat',
    finalEntryPriceExVat: 'finalEntryPriceExVat',
    netEntryPriceExVat: 'netEntryPriceExVat',
    listPriceRonExVat: 'listPriceRonExVat',
    listPriceEuroExVat: 'listPriceEuroExVat',
    furnizorCode: 'furnizorCode',
    furnizorName: 'furnizorName',
    usesManufacturerAsFurnizor: 'furnizorCode',
    isDefault: 'isDefault',
});

/**
 * @param {string} path
 * @returns {{
 *   fieldKey: string|null,
 *   furnizorKey: string|null,
 *   productField: boolean,
 * }}
 */
export function parseCompareChangePath(path) {
    if (!path || typeof path !== 'string') {
        return { fieldKey: null, furnizorKey: null, productField: false };
    }

    const direct = PRODUCT_PATH_TO_FIELD[path];
    if (direct) {
        return { fieldKey: direct, furnizorKey: null, productField: true };
    }

    if (path.startsWith('category_assignments')) {
        return { fieldKey: 'systems', furnizorKey: null, productField: true };
    }

    const furnizorMatch = path.match(
        /^pricing_detail\.furnizorPricings\.([^.]+)(?:\.(.+))?$/,
    );
    if (furnizorMatch) {
        const furnizorKey = furnizorMatch[1];
        const leaf = furnizorMatch[2] || '';
        if (!leaf) {
            return {
                fieldKey: '__furnizor_tab__',
                furnizorKey,
                productField: false,
            };
        }
        if (leaf.startsWith('discounts[')) {
            const indexMatch = leaf.match(/^discounts\[(\d+)\]/);
            if (indexMatch) {
                return {
                    fieldKey: `discounts.${indexMatch[1]}`,
                    furnizorKey,
                    productField: false,
                };
            }
        }
        const mapped = PRICING_DETAIL_LEAF_TO_FIELD[leaf] || leaf;
        return {
            fieldKey: mapped,
            furnizorKey,
            productField: false,
        };
    }

    if (path.startsWith('pricing_detail.')) {
        const leaf = path.slice('pricing_detail.'.length).split('.')[0];
        const mapped = PRICING_DETAIL_LEAF_TO_FIELD[leaf] || leaf;
        return {
            fieldKey: mapped,
            furnizorKey: null,
            productField: true,
        };
    }

    if (path.startsWith('item.')) {
        const leaf = path.slice('item.'.length);
        const mapped = PRODUCT_PATH_TO_FIELD[`item.${leaf}`] || leaf;
        return { fieldKey: mapped, furnizorKey: null, productField: true };
    }

    return { fieldKey: null, furnizorKey: null, productField: false };
}

/**
 * @param {Array<{ path?: string }>|null|undefined} changes
 * @returns {{
 *   productFields: Set<string>,
 *   furnizorFieldsByKey: Map<string, Set<string>>,
 *   changedFurnizorKeys: Set<string>,
 * }}
 */
export function buildCompareHighlightIndex(changes) {
    const productFields = new Set();
    /** @type {Map<string, Set<string>>} */
    const furnizorFieldsByKey = new Map();
    const changedFurnizorKeys = new Set();

    for (const change of changes || []) {
        const parsed = parseCompareChangePath(change?.path);
        if (!parsed.fieldKey) {
            continue;
        }
        if (parsed.furnizorKey) {
            changedFurnizorKeys.add(parsed.furnizorKey);
            if (!furnizorFieldsByKey.has(parsed.furnizorKey)) {
                furnizorFieldsByKey.set(parsed.furnizorKey, new Set());
            }
            furnizorFieldsByKey.get(parsed.furnizorKey).add(parsed.fieldKey);
            continue;
        }
        productFields.add(parsed.fieldKey);
    }

    return { productFields, furnizorFieldsByKey, changedFurnizorKeys };
}

/**
 * Resolves whether a builder field should highlight for the active furnizor tab.
 *
 * @param {{
 *   productFields: Set<string>,
 *   furnizorFieldsByKey: Map<string, Set<string>>,
 *   changedFurnizorKeys: Set<string>,
 * }} index
 * @param {string} fieldKey
 * @param {{
 *   clientKey?: string|null,
 *   furnizorCode?: string|null,
 *   id?: string|number|null,
 * }|null} activeTab
 * @returns {boolean}
 */
export function isCompareFieldHighlightedForTab(index, fieldKey, activeTab = null) {
    if (!index || !fieldKey) {
        return false;
    }
    if (index.productFields.has(fieldKey)) {
        return true;
    }

    const candidates = [];
    const clientKey = String(activeTab?.clientKey || '').trim();
    const code = String(activeTab?.furnizorCode || '').trim();
    if (clientKey) {
        candidates.push(`client:${clientKey}`);
    }
    if (code) {
        candidates.push(`code:${code}`);
    }

    for (const key of candidates) {
        const fields = index.furnizorFieldsByKey.get(key);
        if (fields?.has(fieldKey) || fields?.has('__furnizor_tab__')) {
            return true;
        }
    }

    // Fallback: if only one furnizor changed, apply its field highlights
    // even when key prefixes differ slightly (code vs client).
    if (index.changedFurnizorKeys.size === 1) {
        const onlyKey = [...index.changedFurnizorKeys][0];
        const fields = index.furnizorFieldsByKey.get(onlyKey);
        if (fields?.has(fieldKey) || fields?.has('__furnizor_tab__')) {
            return true;
        }
    }

    return false;
}

/**
 * @param {{
 *   changedFurnizorKeys: Set<string>,
 * }|null|undefined} index
 * @param {{
 *   clientKey?: string|null,
 *   furnizorCode?: string|null,
 * }|null} tab
 * @returns {boolean}
 */
export function isCompareFurnizorTabHighlighted(index, tab) {
    if (!index?.changedFurnizorKeys?.size || !tab) {
        return false;
    }
    const clientKey = String(tab.clientKey || '').trim();
    const code = String(tab.furnizorCode || '').trim();
    if (clientKey && index.changedFurnizorKeys.has(`client:${clientKey}`)) {
        return true;
    }
    if (code && index.changedFurnizorKeys.has(`code:${code}`)) {
        return true;
    }
    return false;
}
